import { useCallback } from 'react';
import { ReactComponent as SmallLogo } from '../assets/LogoMini.svg';
import { ReactComponent as ChevronL } from '../assets/ChevronL.svg';
import { ReactComponent as Logo } from '../assets/Logo.svg';
import { ReactComponent as Ellipsis } from '../assets/Ellipsis.svg';
import { ReactComponent as HorizontalBars } from '../assets/HorizontalBars.svg';
import { useQuestionnaireNavigation } from '../context/NavigationProvider';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../types/graph';
import { navigateToPreviousQuestion } from '../reducers/graph';
import { useInfoQuestionBackwardNavigation } from '../hooks/useInfoQuestionChecker';
import { useShouldHideBackButton } from '../hooks/useShouldHideBackButton';

import { ProgressBar } from './ProgressBar';

export interface HeaderProps {
  variant?: 'default' | 'questionnaire' | 'confirmation' | 'landing';
  onOpenMenu: () => void;
  disableBack?: boolean;
  display?: 'mobile' | 'desktop';
}

export const useHandleBackClick = () => {
  const { navigatePrevious, setHasFreshSelection } =
    useQuestionnaireNavigation();
  const location = useLocation();
  const dispatch = useDispatch();

  const displayFullQueue = useSelector(
    (state: RootState) => state.graph.options.DISPLAY_FULL_QUEUE,
  );

  const currentQuestionIndex = useSelector(
    (state: RootState) => state.questionnaire.currentQuestionIndex,
  );

  const { shouldSkipPreviousQuestion } = useInfoQuestionBackwardNavigation();

  const handleBackClick = useCallback(() => {
    if (displayFullQueue) {
      navigatePrevious();
      return;
    }
    setHasFreshSelection(false);
    const isQuestionnairePage = location.pathname.startsWith('/questionnaire/');
    if (currentQuestionIndex > 0 && isQuestionnairePage) {
      if (shouldSkipPreviousQuestion) {
        dispatch(navigateToPreviousQuestion());
      }
      if (shouldSkipPreviousQuestion && currentQuestionIndex === 1) {
        navigatePrevious();
      } else {
        dispatch(navigateToPreviousQuestion());
      }
      return;
    }

    navigatePrevious();
  }, [
    navigatePrevious,
    location,
    currentQuestionIndex,
    dispatch,
    shouldSkipPreviousQuestion,
    displayFullQueue,
    setHasFreshSelection,
  ]);

  return handleBackClick;
};

export const Header = ({
  variant = 'default',
  onOpenMenu,
  display = 'mobile',
}: HeaderProps) => {
  const handleBackClick = useHandleBackClick();
  const shouldHideBackButton = useShouldHideBackButton();

  // Desktop Header
  if (display === 'desktop') {
    return (
      <div className="flex w-full h-20 items-center justify-between mb-2 px-12">
        <div className="flex items-center w-40 lg:w-30 min-w-[5rem]">
          {variant === 'questionnaire' && <Logo className="h-6" />}
        </div>
        <div className="flex-1 flex justify-center items-center px-6">
          {variant === 'questionnaire' ? (
            <ProgressBar />
          ) : (
            <Logo className="h-6" />
          )}
        </div>
        <div className="w-40 lg:w-30 flex justify-end min-w-[5rem]">
          <button
            aria-label="Open menu"
            className="cursor-pointer"
            onClick={onOpenMenu}
          >
            <HorizontalBars className="h-6 w-6" />
          </button>
        </div>
      </div>
    );
  }

  // Mobile Header
  return (
    <div className="w-full h-full flex items-center justify-between p-6 z-100">
      <button
        onClick={() => {
          console.log('back click');
          handleBackClick();
        }}
        className={`size-8 cursor-pointer`}
      >
        <ChevronL className={shouldHideBackButton ? 'hidden' : ''} />
      </button>
      {variant === 'questionnaire' ? (
        <>
          <div className="flex-1">
            <ProgressBar />
          </div>
          <div className="flex justify-end w-6">
            <SmallLogo className="cursor-pointer" onClick={onOpenMenu} />
          </div>
        </>
      ) : (
        <>
          <Logo className="h-4.5" />
          <button
            aria-label="Open menu"
            onClick={onOpenMenu}
            className="cursor-pointer size-8 rounded-lg flex items-center justify-center"
          >
            <Ellipsis />
          </button>
        </>
      )}
    </div>
  );
};
